// /admin/handoffs — every conversation across ALL clients that is waiting for a
// human, or already held by one.
//
// This is the platform owner's "is anyone being left hanging right now?" view.
// A conversation parked in `waiting_handoff` that nobody picks up is a client's
// customer being ignored, and neither the client nor we would otherwise notice
// until they complained. The endpoint existed since the orchestrator work; this
// is the first thing to call it.

import { apiClient } from '../../api/client.js'

export type HandoffState = 'waiting_handoff' | 'active_handoff'

export interface ParkedHandoff {
  id: string
  tenant: { id: string; name: string }
  state: HandoffState
  /** When it entered the handoff state — the age is the whole point. */
  parkedSince: string
  contact: string | null
  /** "Keep with a human": excluded from the auto-return-to-bot sweep. */
  sticky: boolean
}

/**
 * GET /admin/handoffs — every conversation across ALL clients currently
 * waiting for a human or held by one, so nobody is left hanging unnoticed.
 */
export async function listParkedHandoffs(): Promise<ParkedHandoff[]> {
  const res = await apiClient.get<{ data: ParkedHandoff[] }>('/admin/handoffs')
  return res.data.data
}

/** Mark a conversation to stay with a human (or release it back to the sweep). */
export async function setHandoffSticky(conversationId: string, sticky: boolean): Promise<void> {
  await apiClient.patch(
    `/admin/conversations/${encodeURIComponent(conversationId)}/handoff-sticky`,
    {
      sticky,
    },
  )
}

/** Whole hours parked, for the age column. */
export function hoursParked(parkedSince: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(parkedSince).getTime()) / 3_600_000))
}

/** How urgent a parked conversation is. Waiting is worse than being held: nobody
 *  has it yet. Thresholds match the handoff-notify sweep's own escalation. */
export function urgencyOf(h: ParkedHandoff, now: Date = new Date()): 'ok' | 'warn' | 'sens' {
  const hours = hoursParked(h.parkedSince, now)
  if (h.state === 'waiting_handoff' && hours >= 4) return 'sens'
  if (h.state === 'waiting_handoff' && hours >= 1) return 'warn'
  return 'ok'
}
